import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslateWordCloudMutation } from '../api/useTranslateWordCloudMutation';
import type { TargetLanguage, WordCloudItem } from '../types';

export interface UseWordCloudTranslationOptions {
  words: WordCloudItem[];
  enabled?: boolean;
}

export interface UseWordCloudTranslationResult {
  /** Whether the browser supports Web Translator API and Language Detector */
  isSupported: boolean;
  /** Currently selected target language */
  targetLanguage: TargetLanguage | null;
  /** Set target language (null to show original) */
  setTargetLanguage: (language: TargetLanguage | null) => void;
  /** Translated words (or original if no translation) */
  translatedWords: WordCloudItem[];
  /** Whether translation is in progress */
  isTranslating: boolean;
  /** Translation error if any */
  error: Error | null;
  /** Reset to original words */
  resetTranslation: () => void;
}

/**
 * Hook for translating WordCloud items using Web Translator API
 * Uses React Query mutation pattern for predictable translation flow
 * Handles language selection and translation state
 */
export const useWordCloudTranslation = ({
  words,
  enabled = true,
}: UseWordCloudTranslationOptions): UseWordCloudTranslationResult => {
  const [targetLanguage, setTargetLanguageState] =
    useState<TargetLanguage | null>(null);

  // Track the last requested language to ensure mutationData matches current selection
  const lastRequestedLangRef = useRef<TargetLanguage | null>(null);

  const mutation = useTranslateWordCloudMutation();

  const {
    isSupported,
    isDetectorAvailable,
    isDetectorLoading,
    isPending,
    data: mutationData,
    error: mutationError,
    detectorError,
  } = mutation;

  // Handle language change - always trigger translation
  const handleSetTargetLanguage = useCallback(
    (lang: TargetLanguage | null) => {
      setTargetLanguageState(lang);

      if (!lang || !enabled || !isSupported || words.length === 0) {
        lastRequestedLangRef.current = null;
        return;
      }

      // Wait for detector to be ready
      if (!isDetectorAvailable) {
        return;
      }

      // Track requested language and always translate
      lastRequestedLangRef.current = lang;
      mutation.mutate({
        keywords: words.map((w) => w.keyword),
        targetLanguage: lang,
      });
    },
    [words, enabled, isSupported, isDetectorAvailable, mutation],
  );

  // Re-trigger translation when detector becomes available
  useEffect(() => {
    if (
      targetLanguage &&
      isDetectorAvailable &&
      !isPending &&
      enabled &&
      isSupported
    ) {
      lastRequestedLangRef.current = targetLanguage;
      mutation.mutate({
        keywords: words.map((w) => w.keyword),
        targetLanguage,
      });
    }
    // Only re-run when detector becomes available, not on every dependency change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetectorAvailable]);

  // Apply translations only when mutationData matches the current targetLanguage
  const translatedWords = useMemo(() => {
    if (!targetLanguage || !mutationData) {
      return words;
    }
    // Only apply if the mutation result is for the current requested language
    if (lastRequestedLangRef.current !== targetLanguage) {
      return words;
    }
    return words.map((word) => {
      const translated = mutationData.translations.get(word.keyword);
      if (translated) {
        return {
          ...word,
          keyword: translated,
          originalKeyword: word.keyword,
        };
      }
      return word;
    });
  }, [words, targetLanguage, mutationData]);

  const resetTranslation = useCallback(() => {
    setTargetLanguageState(null);
    lastRequestedLangRef.current = null;
    mutation.reset();
  }, [mutation]);

  // Combine errors
  const combinedError = mutationError ?? detectorError;

  return {
    isSupported,
    targetLanguage,
    setTargetLanguage: handleSetTargetLanguage,
    translatedWords: translatedWords.length > 0 ? translatedWords : words,
    isTranslating: isPending || isDetectorLoading,
    error: combinedError,
    resetTranslation,
  };
};
